import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, Text, VStack, HStack, Box, Badge, Input, FormControl, FormLabel, FormHelperText,
  Table, Thead, Tbody, Tr, Th, Td, Alert, AlertIcon, Spinner, Link, Code, Divider, Tooltip,
  Wrap, WrapItem, Tag, TagLabel, Image, Spacer, SimpleGrid, Select, NumberInput, NumberInputField,
} from "@chakra-ui/react";
import { ExternalLinkIcon, ArrowBackIcon, CheckIcon, CloseIcon, MinusIcon, EditIcon } from "@chakra-ui/icons";
import { getBackendURL } from "../../components/env";
import { supabasePublic } from "../../components/supabaseClient";
import { isImpersonating } from "../../utils/impersonation";

// Dispatches the Browserbase Playwright suite (separate repo) through
// /api/admin/e2e/*. Two screens: the run list (run configuration card — target
// + test patient, each edited in its own small modal and remembered per
// browser — test picker, recent runs that refresh themselves) and a run's
// output (job steps while it runs, then the per-test summary, failure
// screenshots and the suite's console log from the e2e-logs artifact). See
// backend/api/e2e_tests.py.

const STATUS_POLL_MS = 6000; // one GitHub call per poll while a run is live
const HISTORY_POLL_MS = 15000;
const HISTORY_LIMIT = 10; // recent runs shown; older ones are on GitHub's Actions tab
const LOG_POLL_EVERY = 3; // status polls between best-effort live log fetches
const NOT_FOUND_GIVE_UP = 20; // polls (~2 min) before we stop waiting for GitHub to list a dispatched run
const PENDING_ROW_TTL_MS = 3 * 60 * 1000; // a dispatched run GitHub still hasn't listed stays in the table this long
// The test patient every data-changing test creates. Blank = the suite's
// test-patient.env (config.test_patient shows those). Remembered per browser.
const PATIENT_STORAGE_KEY = "e2e_test_patient";
// Where the suite runs: the deployment to drive and the tenant Login must land
// on. Remembered per browser; null = never saved, use the backend's defaults.
const SETTINGS_STORAGE_KEY = "e2e_test_settings";
const SETTINGS_FIELDS = ["notesai_url", "test_schema"];
const EMPTY_PATIENT = { first: "", last: "", date: "", time: "", dob: "", type: "", linger_s: "" };
const PATIENT_FIELDS = ["first", "last", "date", "time", "dob", "type", "linger_s"];
const HOURS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const MINUTES = ["00", "15", "30", "45"];
// What each test file in the suite does (its defineTest name + side effects
// and skip conditions, from the BrowserBase repo's README as of 2026-09-21).
// The chip list itself comes from the repo at the dispatch ref (config.tests),
// so a test missing here still shows — just without a tooltip. Keep in step
// with the suite.
const TEST_INFO = {
  login: {
    name: "Login",
    about: "Signs in as the test doctor and checks the tenant (Expected schema). Gate test: always runs first; the rest are skipped if it fails.",
  },
  "add-patient": {
    name: "Add a patient",
    about: "Creates the test patient on the schedule (a visit row, not a roster entry), finds it by EHR ID, removes it.",
  },
  "send-form": {
    name: "Send a form",
    about: "Creates the test patient as type NP, sends intake forms from Admin Page → Send Reminders. Emails the test form address.",
  },
  "confirm-appointment": {
    name: "Send appointment confirmation",
    about: "Schedules the test patient tomorrow (confirmations need an upcoming visit) and sends the confirmation SMS to the test phone. Skipped when TEST_PHONE is not set in the suite's secrets; fails if that number is on a Do Not Text list.",
  },
  "write-doc": {
    name: "Write inside a doc",
    about: "Opens the test patient's chart, types a short SOAP-style note, waits for autosave, saves a named version, reloads and checks every line plus the MANUAL version.",
  },
  "record-visit": {
    name: "Record audio for a visit",
    about: "Opens Record Visit with a fake microphone playing a 16 s fixture clip, Stop & Save, checks the Audios view lists the recording, then deletes it (Audio Manager + the storage object). No transcript is saved.",
  },
  "check-transcript": {
    name: "Check a transcript exists",
    about: "Schedule → Transcribe Audio: uploads the fixture clip and transcribes it with Corti (uses the test account's credits, ~80 s — the slowest test), then checks the transcript on the chart. Skipped on freemium accounts, or when the account lacks Corti access and TEST_TRANSCRIPTION_PASSWORD is not set.",
  },
};

function loadStoredPatient() {
  try {
    const raw = JSON.parse(localStorage.getItem(PATIENT_STORAGE_KEY) || "{}");
    return Object.fromEntries(PATIENT_FIELDS.map((k) => [k, typeof raw[k] === "string" ? raw[k] : ""]));
  } catch (e) {
    return { ...EMPTY_PATIENT };
  }
}

function loadStoredSettings() {
  try {
    const raw = JSON.parse(localStorage.getItem(SETTINGS_STORAGE_KEY) || "null");
    if (!raw || typeof raw !== "object") return null;
    return Object.fromEntries(SETTINGS_FIELDS.map((k) => [k, typeof raw[k] === "string" ? raw[k] : ""]));
  } catch (e) {
    return null;
  }
}

function storeSettings(settings) {
  try {
    if (settings) localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    else localStorage.removeItem(SETTINGS_STORAGE_KEY);
  } catch (e) {
    // private mode etc.
  }
}

function storePatient(patient) {
  try {
    localStorage.setItem(PATIENT_STORAGE_KEY, JSON.stringify(patient));
  } catch (e) {
    // private mode etc. — the value still lives in state for this session
  }
}

// summary.json names tests by their defineTest name; the chips use file names.
function fileForTest(name) {
  return Object.keys(TEST_INFO).find((k) => TEST_INFO[k].name === name) || null;
}

// "10:00 AM" → { hour: "10", minute: "00", ampm: "AM" } (the dialog's three selects).
function splitTime(value) {
  const m = /^(0?[1-9]|1[0-2]):(00|15|30|45) ?([AaPp][Mm])$/.exec((value || "").trim());
  return m ? { hour: String(Number(m[1])), minute: m[2], ampm: m[3].toUpperCase() } : { hour: "", minute: "", ampm: "" };
}
const ACTIVE = new Set(["queued", "in_progress", "waiting", "requested", "pending"]);
// GitHub adds these around the workflow's own steps; not worth a chip.
const HIDDEN_STEP = /^(Set up job|Complete job|Post )/;

async function getToken() {
  const { data: { session } = {} } = await supabasePublic().auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("No active session — sign in again");
  return token;
}

async function apiFetch(path, { method = "GET", body } = {}) {
  const token = await getToken();
  const res = await fetch(`${getBackendURL()}/api/admin/e2e/${path}`, {
    method,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.error) {
    const err = new Error(json.detail || json.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.payload = json;
    throw err;
  }
  return json;
}

// <img src> can't carry the bearer token, so screenshots come in as blobs.
async function apiFetchBlobUrl(path) {
  const token = await getToken();
  const res = await fetch(`${getBackendURL()}/api/admin/e2e/${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return null;
  return URL.createObjectURL(await res.blob());
}

const CONCLUSION_COLOR = {
  success: "green", failure: "red", cancelled: "gray", timed_out: "orange",
  skipped: "gray", neutral: "gray", action_required: "orange", startup_failure: "red",
};

function RunBadge({ run }) {
  if (!run) return null;
  if (run.status === "completed") {
    return <Badge colorScheme={CONCLUSION_COLOR[run.conclusion] || "gray"}>{run.conclusion || "completed"}</Badge>;
  }
  return (
    <Badge colorScheme="blue">
      <HStack spacing={1}><Spinner size="xs" /><span>{(run.status || "").replace("_", " ")}</span></HStack>
    </Badge>
  );
}

const RESULT_COLOR = { PASS: "green", FAIL: "red", SKIP: "gray" };

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString() : "";
}

function fmtTime(date) {
  return date ? date.toLocaleTimeString() : "";
}

function stepSecs(step) {
  if (!step.started_at || !step.completed_at) return null;
  return Math.max(0, Math.round((new Date(step.completed_at) - new Date(step.started_at)) / 1000));
}

// The workflow's steps as a row of chips: ✓ / ✗ / – / spinner / pending.
function StepsBar({ jobs }) {
  const steps = (jobs || []).flatMap((j) => j.steps || []).filter((s) => !HIDDEN_STEP.test(s.name || ""));
  if (steps.length === 0) return null;
  return (
    <Wrap spacing={2}>
      {steps.map((s) => {
        let color = "gray";
        let icon = <Box as="span" w="8px" h="8px" borderRadius="full" borderWidth="1px" borderColor="gray.400" />;
        if (s.status === "in_progress") {
          color = "blue";
          icon = <Spinner size="xs" />;
        } else if (s.status === "completed") {
          if (s.conclusion === "success") { color = "green"; icon = <CheckIcon boxSize={2.5} />; }
          else if (s.conclusion === "failure" || s.conclusion === "timed_out" || s.conclusion === "cancelled") {
            color = "red"; icon = <CloseIcon boxSize={2} />;
          } else { icon = <MinusIcon boxSize={2.5} />; } // skipped
        }
        const secs = stepSecs(s);
        return (
          <WrapItem key={s.number ?? s.name}>
            <Tag size="sm" colorScheme={color} variant="subtle">
              <HStack spacing={1.5}>
                {icon}
                <TagLabel>{s.name}{secs != null ? ` · ${secs}s` : ""}</TagLabel>
              </HStack>
            </Tag>
          </WrapItem>
        );
      })}
    </Wrap>
  );
}

const LINE_COLOR = [
  [/^PASS: /, "green.300"],
  [/^FAIL: /, "red.300"],
  [/^SKIP: /, "gray.400"],
  [/^=== /, "cyan.200"],
  [/^##\[(error|warning)\]/, "orange.300"],
];

// Console output. Follows the tail while the run is live, like a terminal.
function OutputBox({ log, live, empty }) {
  const ref = useRef(null);
  const text = log?.log || "";
  const lines = useMemo(() => (text ? text.split("\n") : []), [text]);
  useEffect(() => {
    if (live && ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [text, live]);
  const tooLong = lines.length > 5000;
  return (
    <Box>
      <HStack mb={1} fontSize="xs" color="gray.500">
        <Text fontWeight="semibold" color="gray.700">Output</Text>
        {log?.source === "artifact" && <Text>suite log (run.log from the e2e-logs artifact)</Text>}
        {log?.source === "job" && <Text>GitHub job log{live ? " — updates as steps finish" : ""}</Text>}
        {log?.truncated && <Badge colorScheme="orange">tail only</Badge>}
      </HStack>
      <Box
        ref={ref}
        bg="gray.900"
        color="gray.100"
        fontFamily="mono"
        fontSize="xs"
        p={3}
        borderRadius="md"
        maxH="420px"
        overflow="auto"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
      >
        {text
          ? tooLong
            ? text
            : lines.map((line, i) => {
                const color = (LINE_COLOR.find(([re]) => re.test(line)) || [])[1];
                return <Box key={i} as="div" color={color} fontWeight={color === "cyan.200" ? "semibold" : undefined}>{line || " "}</Box>;
              })
          : <Text color="gray.500">{empty}</Text>}
      </Box>
    </Box>
  );
}

// A run we just dispatched, shown in the table before GitHub lists it. GitHub
// takes 5-20 s to create the run, so without this the row would only appear
// on a later refresh. `pending` rows are replaced by the real run (matched on
// run_id) and dropped if GitHub never reports one.
function pendingRow(runId, actor) {
  const now = new Date().toISOString();
  return {
    id: null, run_id: runId, title: `e2e ${runId}`, status: "queued", conclusion: null,
    html_url: null, created_at: now, run_started_at: null, updated_at: now,
    actor, head_branch: null, pending: true,
  };
}

function mergeHistory(prev, fetched) {
  const listed = new Set(fetched.map((r) => r.run_id).filter(Boolean));
  const cutoff = Date.now() - PENDING_ROW_TTL_MS;
  const keep = prev.filter((r) => r.pending && !listed.has(r.run_id) && new Date(r.created_at).getTime() > cutoff);
  return [...keep, ...fetched].slice(0, HISTORY_LIMIT);
}

// Where the suite runs. Saving stores the pair; "Use defaults" forgets it so
// the backend's defaults (GITHUB_E2E_DEFAULT_URL, STUDENT) apply again.
function TestSettingsModal({ isOpen, onClose, value, defaults, onSave }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (isOpen) setDraft(value);
  }, [isOpen, value]);

  const url = (draft.notesai_url || "").trim();
  const schema = (draft.test_schema || "").trim();
  const urlBad = !/^https:\/\/[^\s"'<>]+$/.test(url);
  const schemaBad = !/^[A-Za-z0-9_]{0,64}$/.test(schema);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Test settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl isInvalid={urlBad}>
              <FormLabel fontSize="sm">Deployment URL</FormLabel>
              <Input
                size="sm"
                value={draft.notesai_url}
                onChange={(e) => setDraft({ ...draft, notesai_url: e.target.value })}
                placeholder={defaults.notesai_url || "https://app.magicnotesai.com/NotesAI/"}
              />
              <FormHelperText fontSize="xs">
                The NotesAI deployment the suite drives.
              </FormHelperText>
            </FormControl>
            <FormControl isInvalid={schemaBad}>
              <FormLabel fontSize="sm">Expected schema</FormLabel>
              <Input
                size="sm"
                maxW="240px"
                value={draft.test_schema}
                onChange={(e) => setDraft({ ...draft, test_schema: e.target.value })}
                placeholder="any tenant"
              />
              <FormHelperText fontSize="xs">
                The tenant the test account must land on (the header's <Code>Schema:</Code> value). Login fails
                the whole run if it differs — a guard against pointing the suite at the wrong account or
                deployment. Blank accepts any tenant. Letters, digits and underscores only.
              </FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr="auto" onClick={() => setDraft({ ...defaults })}>
            Use defaults
          </Button>
          <Button size="sm" variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            colorScheme="cyan"
            isDisabled={urlBad || schemaBad}
            onClick={() => {
              const next = { notesai_url: url, test_schema: schema };
              const isDefault = SETTINGS_FIELDS.every((k) => next[k] === (defaults[k] || ""));
              onSave(isDefault ? null : next);
              onClose();
            }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// One labelled value in the run-configuration card. Overridden values (set
// in this browser) are highlighted; defaults are muted and say where they
// come from.
function ConfigField({ label, value, overridden, from, minW = "90px" }) {
  return (
    <Tooltip label={overridden ? "Set here" : from} hasArrow placement="top" isDisabled={!from && !overridden}>
      <Box minW={minW}>
        <Text fontSize="10px" textTransform="uppercase" letterSpacing="wide" color="gray.500">{label}</Text>
        <HStack spacing={1}>
          {overridden && <Box as="span" w="6px" h="6px" borderRadius="full" bg="cyan.500" flexShrink={0} />}
          <Text fontSize="sm" color={overridden ? "cyan.700" : "gray.800"} fontWeight={overridden ? "semibold" : "normal"} noOfLines={1}>
            {value || "—"}
          </Text>
        </HStack>
      </Box>
    </Tooltip>
  );
}

// Third modal: the throwaway patient's details. Each field's placeholder is
// the suite's own default (test-patient.env at the dispatch ref); leaving a
// field blank keeps that default.
function TestPatientModal({ isOpen, onClose, value, defaults, onSave }) {
  const [draft, setDraft] = useState(value);
  const [time, setTime] = useState(splitTime(value.time));
  useEffect(() => {
    if (isOpen) {
      setDraft(value);
      setTime(splitTime(value.time));
    }
  }, [isOpen, value]);

  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
  const setT = (k) => (e) => setTime({ ...time, [k]: e.target.value });
  const d = (k) => defaults?.[k] || "";
  const timeComplete = time.hour && time.minute && time.ampm;
  const timePartial = !timeComplete && (time.hour || time.minute || time.ampm);
  const lingerBad = draft.linger_s !== "" && !(/^\d+$/.test(draft.linger_s) && Number(draft.linger_s) <= 120);
  const nameBad = (v) => v && !/^[A-Za-z][A-Za-z .'-]{0,39}$/.test(v);
  const typeBad = draft.type && !/^[A-Za-z0-9_]{1,12}$/.test(draft.type);
  const dateBad = draft.date && !/^(\d{4}-\d{2}-\d{2}|today|yesterday|tomorrow|[+-]\d{1,3})$/i.test(draft.date.trim());
  const invalid = timePartial || lingerBad || nameBad(draft.first) || nameBad(draft.last) || typeBad || dateBad;

  const save = () => {
    const next = { ...draft, time: timeComplete ? `${time.hour}:${time.minute} ${time.ampm}` : "" };
    PATIENT_FIELDS.forEach((k) => { next[k] = (next[k] || "").trim(); });
    onSave(next);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Test patient</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <Text fontSize="sm" color="gray.600">
              The throwaway patient every data-changing test creates (a visit row on the schedule, removed
              afterwards). Blank fields use the suite's <Code>test-patient.env</Code>
              {defaults && Object.keys(defaults).length > 0 ? " — shown as placeholders." : "."}
            </Text>
            <HStack align="flex-start">
              <FormControl isInvalid={Boolean(nameBad(draft.first))}>
                <FormLabel fontSize="sm">First name</FormLabel>
                <Input size="sm" value={draft.first} onChange={set("first")} placeholder={d("first")} />
              </FormControl>
              <FormControl isInvalid={Boolean(nameBad(draft.last))}>
                <FormLabel fontSize="sm">Last name</FormLabel>
                <Input size="sm" value={draft.last} onChange={set("last")} placeholder={d("last")} />
                <FormHelperText fontSize="xs">Shown as "Last, First".</FormHelperText>
              </FormControl>
            </HStack>
            <HStack align="flex-start">
              <FormControl isInvalid={Boolean(dateBad)}>
                <FormLabel fontSize="sm">Schedule date</FormLabel>
                <Input size="sm" value={draft.date} onChange={set("date")} placeholder={d("date") || "yesterday"} />
                <FormHelperText fontSize="xs">YYYY-MM-DD or today / yesterday / tomorrow / +2 / -3.</FormHelperText>
              </FormControl>
              <FormControl isInvalid={Boolean(timePartial)}>
                <FormLabel fontSize="sm">Visit time</FormLabel>
                <HStack spacing={1}>
                  <Select size="sm" value={time.hour} onChange={setT("hour")} placeholder={splitTime(d("time")).hour || "H"}>
                    {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                  </Select>
                  <Select size="sm" value={time.minute} onChange={setT("minute")} placeholder={splitTime(d("time")).minute || "MM"}>
                    {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </Select>
                  <Select size="sm" value={time.ampm} onChange={setT("ampm")} placeholder={splitTime(d("time")).ampm || "AM/PM"}>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </Select>
                </HStack>
                <FormHelperText fontSize="xs">Quarter hours, like the Add Patient dialog.{timePartial ? " Pick all three." : ""}</FormHelperText>
              </FormControl>
            </HStack>
            <HStack align="flex-start">
              <FormControl>
                <FormLabel fontSize="sm">Date of birth</FormLabel>
                <Input size="sm" type="date" value={draft.dob} onChange={set("dob")} placeholder={d("dob")} />
                <FormHelperText fontSize="xs">Default {d("dob") || "1990-01-01"}.</FormHelperText>
              </FormControl>
              <FormControl isInvalid={Boolean(typeBad)}>
                <FormLabel fontSize="sm">Patient type</FormLabel>
                <Input size="sm" value={draft.type} onChange={set("type")} placeholder={d("type")} />
                <FormHelperText fontSize="xs">
                  Type code as typed into the dialog (ST on STUDENT). send-form forces NP regardless.
                </FormHelperText>
              </FormControl>
              <FormControl isInvalid={Boolean(lingerBad)} maxW="150px">
                <FormLabel fontSize="sm">Linger (s)</FormLabel>
                <NumberInput size="sm" min={0} max={120} value={draft.linger_s} onChange={(v) => setDraft({ ...draft, linger_s: v })}>
                  <NumberInputField placeholder="0" />
                </NumberInput>
                <FormHelperText fontSize="xs">
                  Seconds every test in the run leaves its result on screen before cleaning up, to watch in the
                  Browserbase session viewer. Blank or 0 = no pause. Max 120.
                </FormHelperText>
              </FormControl>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr="auto" onClick={() => { setDraft({ ...EMPTY_PATIENT }); setTime(splitTime("")); }}>
            Clear (use suite defaults)
          </Button>
          <Button size="sm" variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="cyan" onClick={save} isDisabled={Boolean(invalid)}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function E2ETestsModal({ isOpen, onClose }) {
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState("");
  const [tests, setTests] = useState("");
  const [settings, setSettings] = useState(loadStoredSettings); // null = backend defaults
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [patient, setPatient] = useState(loadStoredPatient);
  const [patientOpen, setPatientOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState("list"); // "list" | "run"
  const [history, setHistory] = useState([]);
  const [historyAt, setHistoryAt] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  // The run being shown: { runId (sa-…), id (GitHub), run, jobs, summary, found, detailed }
  const [sel, setSel] = useState(null);
  const [log, setLog] = useState(null); // { log, source, truncated, complete }
  const [logLoading, setLogLoading] = useState(false);
  const [shots, setShots] = useState({}); // screenshot name → object URL
  const historyTimer = useRef(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const json = await apiFetch(`runs/?limit=${HISTORY_LIMIT}`);
      setHistory((prev) => mergeHistory(prev, json.runs || []));
      setHistoryAt(new Date());
    } catch (e) {
      // history is best-effort; the main error banner covers config problems
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Open: load config, then keep the run list fresh on its own.
  useEffect(() => {
    if (!isOpen) return undefined;
    setError("");
    setConfigError("");
    setView("list");
    setSel(null);
    setLog(null);
    setShots({});
    let cancelled = false;
    (async () => {
      try {
        const cfg = await apiFetch("config/");
        if (cancelled) return;
        setConfig(cfg);
        if (cfg.configured) {
          loadHistory();
          historyTimer.current = setInterval(loadHistory, HISTORY_POLL_MS);
        }
      } catch (e) {
        if (!cancelled) setConfigError(e.message);
      }
    })();
    return () => {
      cancelled = true;
      if (historyTimer.current) {
        clearInterval(historyTimer.current);
        historyTimer.current = null;
      }
    };
  }, [isOpen, loadHistory]);

  // Revoke screenshot blob URLs when they are replaced or the modal unmounts.
  useEffect(() => () => Object.values(shots).forEach((u) => URL.revokeObjectURL(u)), [shots]);

  const selDone = Boolean(sel?.found && sel.run && !ACTIVE.has(sel.run.status));
  const pollKey = sel ? `${sel.runId || ""}|${sel.id || ""}` : null;

  const fetchLog = useCallback(async (id) => {
    setLogLoading(true);
    try {
      const json = await apiFetch(`log/?id=${id}`);
      setLog((prev) => (json.log == null && prev?.log ? prev : json));
    } catch (e) {
      // best-effort; the steps bar still shows progress
    } finally {
      setLogLoading(false);
    }
  }, []);

  // Live polling of the selected run until GitHub reports it completed. Runs
  // on the list screen too, so a just-dispatched run's placeholder row turns
  // into the real run without waiting for the list refresh.
  useEffect(() => {
    if (!isOpen || !sel || selDone) return undefined;
    const { runId, id } = sel;
    let cancelled = false;
    let n = 0;
    const tick = async () => {
      try {
        const q = id ? `id=${id}` : `run_id=${encodeURIComponent(runId)}`;
        const json = await apiFetch(`status/?${q}`);
        if (cancelled) return;
        n += 1;
        if (!json.found) {
          if (n >= NOT_FOUND_GIVE_UP) {
            clearInterval(t);
            setError(
              "GitHub has not listed this run after two minutes — the dispatch may have been dropped. " +
              "Check the Actions tab on GitHub (and that GITHUB_E2E_REF names a branch that has the workflow).",
            );
          }
          return;
        }
        setSel((prev) => ({ ...prev, ...json, runId: json.run.run_id || prev?.runId, id: json.run.id, detailed: true }));
        setHistory((prev) => {
          const i = prev.findIndex((r) => (json.run.run_id && r.run_id === json.run.run_id) || r.id === json.run.id);
          if (i < 0) return [json.run, ...prev].slice(0, HISTORY_LIMIT);
          const next = prev.slice();
          next[i] = json.run;
          return next;
        });
        if (json.run.status === "completed") {
          loadHistory(); // the finished-run effect fetches the artifact log
          return;
        }
        const suiteRunning = (json.jobs || []).some((j) =>
          (j.steps || []).some((s) => s.status === "in_progress" && /suite/i.test(s.name || "")));
        if (view === "run" && suiteRunning && n % LOG_POLL_EVERY === 1) fetchLog(json.run.id);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    const t = setInterval(tick, STATUS_POLL_MS);
    tick();
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, view, pollKey, selDone, loadHistory, fetchLog]);

  // A finished run (opened from the list, or just completed while followed):
  // one status fetch for jobs + summary if we don't have them, then the suite log.
  useEffect(() => {
    if (!isOpen || view !== "run" || !sel || !selDone || !sel.id) return undefined;
    let cancelled = false;
    (async () => {
      if (!sel.detailed) {
        try {
          const json = await apiFetch(`status/?id=${sel.id}`);
          if (cancelled) return;
          if (json.found) setSel((prev) => ({ ...prev, ...json, id: json.run.id, detailed: true }));
        } catch (e) {
          if (!cancelled) setError(e.message);
        }
      }
      if (!cancelled && !log?.complete) fetchLog(sel.id);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, view, sel?.id, selDone, sel?.detailed]);

  // Failure screenshots for the summary being shown.
  const shotNames = useMemo(
    () => (sel?.summary?.tests || []).filter((t) => t.status === "FAIL" && t.screenshot).map((t) => t.screenshot),
    [sel?.summary],
  );
  useEffect(() => {
    if (!isOpen || !sel?.id || shotNames.length === 0) return undefined;
    let cancelled = false;
    const id = sel.id;
    (async () => {
      const entries = await Promise.all(
        shotNames.map(async (name) => [name, await apiFetchBlobUrl(`screenshot/?id=${id}&name=${encodeURIComponent(name)}`)]),
      );
      if (cancelled) {
        entries.forEach(([, u]) => u && URL.revokeObjectURL(u));
        return;
      }
      setShots(Object.fromEntries(entries.filter(([, u]) => u)));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sel?.id, shotNames.join(",")]);

  const openRun = (run, extra = {}) => {
    setError("");
    setLog(null);
    setShots({});
    setSel({ found: !run.pending, runId: run.run_id, id: run.id, run, jobs: [], summary: null, detailed: false, ...extra });
    setView("run");
  };

  const handleRun = async () => {
    setLaunching(true);
    setError("");
    try {
      const { date, ...rest } = patient;
      const json = await apiFetch("run/", {
        method: "POST",
        body: { ...effSettings, tests, patient_date: date, patient: rest },
      });
      setHistory((prev) => [pendingRow(json.run_id, "you"), ...prev].slice(0, HISTORY_LIMIT));
      setLog(null);
      setShots({});
      setSel({ found: false, runId: json.run_id, id: null, run: null, jobs: [], summary: null, detailed: false });
      setView("run");
    } catch (e) {
      if (e.status === 409 && e.payload?.run) {
        openRun(e.payload.run);
        setError("A run is already in progress — showing it instead. Wait for it to finish before starting another.");
      } else {
        setError(e.message);
      }
    } finally {
      setLaunching(false);
    }
  };

  const toggleTest = (name) => {
    const names = tests.replace(/,/g, " ").split(/\s+/).filter(Boolean);
    const next = names.includes(name) ? names.filter((n) => n !== name) : [...names, name];
    setTests(next.join(" "));
  };
  const selectedTests = new Set(tests.replace(/,/g, " ").split(/\s+/).filter(Boolean));

  const settingsDefaults = {
    notesai_url: config?.defaults?.notesai_url || "https://app.magicnotesai.com/NotesAI/",
    test_schema: config?.defaults?.test_schema ?? "STUDENT",
  };
  const effSettings = settings || settingsDefaults;
  const settingsOverridden = Boolean(settings);
  const targetHost = (() => {
    try { return new URL(effSettings.notesai_url).host; } catch (e) { return effSettings.notesai_url; }
  })();

  const patientDefaults = config?.test_patient || {};
  const overriddenCount = PATIENT_FIELDS.filter((k) => patient[k]).length;
  // Built-ins mirror tests/patients.js for when test-patient.env could not be read.
  const eff = (k, builtin) => patient[k] || patientDefaults[k] || builtin;
  const patientFrom = (k) => (patientDefaults[k] ? "Suite default (test-patient.env)" : "Suite built-in default");
  const pf = (k, builtin) => ({ value: eff(k, builtin), overridden: Boolean(patient[k]), from: patientFrom(k) });
  const nameField = {
    value: `${eff("last", "L")}, ${eff("first", "Wyatt")}`,
    overridden: Boolean(patient.first || patient.last),
    from: patientFrom("first"),
  };

  const activeInHistory = history.find((r) => ACTIVE.has(r.status));
  const busy = launching || Boolean(activeInHistory) || Boolean(sel && !selDone);
  const readOnly = isImpersonating();
  const summary = sel?.summary;

  const runView = sel && (
    <VStack align="stretch" spacing={4}>
      <HStack flexWrap="wrap" spacing={3}>
        <Button size="sm" variant="ghost" leftIcon={<ArrowBackIcon />} onClick={() => setView("list")}>
          All runs
        </Button>
        {sel.found && sel.run ? (
          <>
            <RunBadge run={sel.run} />
            <Text fontSize="sm" fontWeight="semibold">{sel.run.title}</Text>
            <Text fontSize="xs" color="gray.500">
              started {fmt(sel.run.run_started_at || sel.run.created_at)}
              {sel.run.actor ? ` · by ${sel.run.actor}` : ""}
              {sel.run.head_branch ? ` · ${sel.run.head_branch}` : ""}
            </Text>
          </>
        ) : (
          <>
            <Badge colorScheme="blue"><HStack spacing={1}><Spinner size="xs" /><span>dispatched</span></HStack></Badge>
            <Text fontSize="sm" fontWeight="semibold">e2e {sel.runId}</Text>
          </>
        )}
        <Spacer />
        {sel.run?.html_url && (
          <Link href={sel.run.html_url} isExternal fontSize="sm" color="blue.500">
            Open on GitHub <ExternalLinkIcon mx="2px" />
          </Link>
        )}
      </HStack>

      {error && (
        <Alert status="warning"><AlertIcon /><Text fontSize="sm">{error}</Text></Alert>
      )}

      {!sel.found && (
        <HStack fontSize="sm" color="gray.600">
          <Spinner size="sm" />
          <Text>Waiting for GitHub to pick up the dispatch — usually 5–20 s…</Text>
        </HStack>
      )}

      <StepsBar jobs={sel.jobs} />

      {selDone && !summary && sel.detailed && (
        <Text fontSize="sm" color="gray.600">
          No per-test summary was published for this run — the suite did not get as far as writing summary.json.
          The output below (or the run on GitHub) says why.
        </Text>
      )}

      {summary && (
        <Box borderWidth="1px" borderRadius="md" p={3}>
          <HStack spacing={3} mb={2} fontSize="sm">
            <Badge colorScheme="green">{summary.passed ?? 0} passed</Badge>
            <Badge colorScheme="red">{summary.failed ?? 0} failed</Badge>
            <Badge colorScheme="gray">{summary.skipped ?? 0} skipped</Badge>
            {summary.secs != null && <Text color="gray.500">{summary.secs}s</Text>}
          </HStack>
          {summary.aborted && (
            <Alert status="error" size="sm" mb={2}><AlertIcon />Suite aborted: {summary.aborted}</Alert>
          )}
          <Table size="sm" variant="simple">
            <Thead>
              <Tr><Th>Test</Th><Th>Result</Th><Th isNumeric>Time</Th><Th>Detail</Th></Tr>
            </Thead>
            <Tbody>
              {(summary.tests || []).map((t) => (
                <Tr key={t.name}>
                  <Td>
                    {t.name}
                    {fileForTest(t.name) && <Code ml={2} fontSize="xs">{fileForTest(t.name)}</Code>}
                  </Td>
                  <Td><Badge colorScheme={RESULT_COLOR[t.status] || "gray"}>{t.status}</Badge></Td>
                  <Td isNumeric>{t.secs != null ? `${t.secs}s` : ""}</Td>
                  <Td fontSize="xs" color={t.status === "FAIL" ? "red.600" : "gray.600"} whiteSpace="pre-wrap">
                    {t.error || ""}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {shotNames.length > 0 && (
        <Box>
          <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={1}>Failure screenshots</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            {shotNames.map((name) => (
              <Box key={name} borderWidth="1px" borderRadius="md" overflow="hidden">
                <Text fontSize="xs" px={2} py={1} bg="gray.50" color="gray.600">{name}</Text>
                {shots[name] ? (
                  <Link href={shots[name]} isExternal>
                    <Image src={shots[name]} alt={name} w="100%" />
                  </Link>
                ) : (
                  <HStack p={3} fontSize="xs" color="gray.500"><Spinner size="xs" /><Text>loading…</Text></HStack>
                )}
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      )}

      <OutputBox
        log={log}
        live={!selDone}
        empty={
          !sel.found
            ? "Nothing yet."
            : selDone
              ? logLoading ? "Loading…" : "No log was published for this run."
              : "Waiting for output — GitHub exposes a step's log once it finishes; the suite's own log arrives with the artifact."
        }
      />
    </VStack>
  );

  const listView = (
    <VStack align="stretch" spacing={4}>
      {configError && (
        <Alert status="error"><AlertIcon />{configError}</Alert>
      )}
      {config && !config.configured && (
        <Alert status="warning">
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">E2E runner is not configured on this backend.</Text>
            <Text fontSize="sm">Missing: {config.missing?.join(", ")}. Set them in backend/.env and on Render.</Text>
          </Box>
        </Alert>
      )}
      {config?.configured && (
        <Text fontSize="sm" color="gray.600">
          Runs <Code>{config.workflow}</Code> on <Code>{config.repo}</Code> @ <Code>{config.ref}</Code> via GitHub
          Actions ({config.auth === "app" ? "GitHub App" : "token"}).
          {config.ref && config.ref !== "main" && (
            <> <Badge colorScheme="orange">ref is {config.ref}</Badge> — the button runs whatever that branch holds;
            the suite is maintained on <Code>main</Code> (GITHUB_E2E_REF).</>
          )}
        </Text>
      )}

      {config && (
      <Box borderWidth="1px" borderRadius="md" overflow="hidden">
        {/* Target: where the suite runs */}
        <HStack px={3} py={2} spacing={3} flexWrap="wrap" bg="gray.50">
          <Text fontSize="xs" fontWeight="semibold" color="gray.600" minW="80px">Target</Text>
          <Tooltip label={effSettings.notesai_url} hasArrow placement="top">
            <Link href={effSettings.notesai_url} isExternal fontSize="sm" fontFamily="mono" onClick={(e) => e.stopPropagation()}>
              {targetHost} <ExternalLinkIcon mx="2px" boxSize={3} />
            </Link>
          </Tooltip>
          <Tooltip
            hasArrow
            placement="top"
            label={effSettings.test_schema
              ? `Login must land on the ${effSettings.test_schema} tenant or the run fails at the gate`
              : "Any tenant the test account belongs to is accepted"}
          >
            <Badge colorScheme={effSettings.test_schema ? "purple" : "gray"} variant="subtle">
              schema: {effSettings.test_schema || "any"}
            </Badge>
          </Tooltip>
          {settingsOverridden && <Badge colorScheme="cyan">custom</Badge>}
          <Spacer />
          <Button size="xs" variant="outline" leftIcon={<EditIcon />} onClick={() => setSettingsOpen(true)}>
            Settings
          </Button>
        </HStack>

        <Divider />

        {/* Test patient: what every data-changing test creates */}
        <Box px={3} py={2}>
          <HStack spacing={3} mb={2} flexWrap="wrap">
            <Text fontSize="xs" fontWeight="semibold" color="gray.600" minW="80px">Test patient</Text>
            <Text fontSize="xs" color="gray.500">
              A visit row every data-changing test adds to the schedule and removes afterwards.
            </Text>
            <Spacer />
            {overriddenCount > 0 && <Badge colorScheme="cyan">{overriddenCount} overridden</Badge>}
            <Button size="xs" variant="outline" leftIcon={<EditIcon />} onClick={() => setPatientOpen(true)}>
              Edit
            </Button>
          </HStack>
          <Wrap spacing={6} pl={{ base: 0, md: "92px" }}>
            <WrapItem><ConfigField label="Name" minW="120px" {...nameField} /></WrapItem>
            <WrapItem><ConfigField label="Schedule date" {...pf("date", "yesterday")} /></WrapItem>
            <WrapItem><ConfigField label="Time" {...pf("time", "10:00 AM")} /></WrapItem>
            <WrapItem><ConfigField label="DOB" {...pf("dob", "1990-01-01")} /></WrapItem>
            <WrapItem><ConfigField label="Type" minW="50px" {...pf("type", "ST")} /></WrapItem>
            <WrapItem>
              <ConfigField
                label="Linger"
                minW="60px"
                value={patient.linger_s ? `${patient.linger_s}s` : "none"}
                overridden={Boolean(patient.linger_s)}
                from="The suite never pauses on a dispatched run unless asked"
              />
            </WrapItem>
          </Wrap>
        </Box>
      </Box>
      )}

      <FormControl>
        <HStack mb={1}>
          <FormLabel fontSize="sm" mb={0}>Tests (optional)</FormLabel>
          {config?.tests?.length > 0 && (
            <>
              <Spacer />
              <Button size="xs" variant="link" onClick={() => setTests(config.tests.join(" "))}>
                select all
              </Button>
              <Button size="xs" variant="link" onClick={() => setTests("")} isDisabled={!tests}>
                clear
              </Button>
            </>
          )}
        </HStack>
        {config?.tests?.length > 0 && (
          <Wrap spacing={2} mb={2}>
            {config.tests.map((name) => {
              const info = TEST_INFO[name];
              return (
                <WrapItem key={name}>
                  <Tooltip
                    hasArrow
                    placement="top"
                    isDisabled={!info}
                    label={info ? <Box><Text fontWeight="semibold">{info.name}</Text><Text>{info.about}</Text></Box> : ""}
                  >
                    <Tag
                      size="sm"
                      cursor="pointer"
                      colorScheme={selectedTests.has(name) ? "cyan" : "gray"}
                      variant={selectedTests.has(name) ? "solid" : "subtle"}
                      onClick={() => toggleTest(name)}
                    >
                      <TagLabel>{name}</TagLabel>
                    </Tag>
                  </Tooltip>
                </WrapItem>
              );
            })}
          </Wrap>
        )}
        <Input
          size="sm"
          maxW="420px"
          value={tests}
          onChange={(e) => setTests(e.target.value)}
          placeholder="login send-form"
        />
        <FormHelperText fontSize="xs">
          Space-separated file names from the suite's tests/ folder (hover a chip for what it does). Blank = all.
          Login always runs first and gates the rest; the others run in parallel, one Browserbase session each.
        </FormHelperText>
      </FormControl>

      <HStack>
        <Tooltip label="Read-only while viewing as another user" isDisabled={!readOnly} hasArrow>
          <Button
            colorScheme="cyan"
            onClick={handleRun}
            isLoading={launching}
            isDisabled={readOnly || !config?.configured || busy}
          >
            Run E2E suite
          </Button>
        </Tooltip>
        {activeInHistory && !launching && (
          <HStack fontSize="sm" color="gray.600">
            <Spinner size="sm" />
            <Text>{activeInHistory.title} is {(activeInHistory.status || "").replace("_", " ")}</Text>
            <Button size="xs" variant="outline" onClick={() => openRun(activeInHistory)}>View</Button>
          </HStack>
        )}
      </HStack>

      {error && (
        <Alert status="error"><AlertIcon /><Text fontSize="sm">{error}</Text></Alert>
      )}

      {config?.configured && (
        <>
          <Divider />
          <HStack justify="space-between">
            <Text fontWeight="semibold" fontSize="sm">Recent runs <Text as="span" fontWeight="normal" color="gray.500">(last {HISTORY_LIMIT})</Text></Text>
            <HStack fontSize="xs" color="gray.500" spacing={2}>
              {historyLoading && <Spinner size="xs" />}
              <Text>{historyAt ? `updated ${fmtTime(historyAt)}` : ""} · refreshes every {HISTORY_POLL_MS / 1000}s</Text>
            </HStack>
          </HStack>
          {history.length === 0 ? (
            <Text fontSize="sm" color="gray.500">{historyAt ? "No dispatched runs yet." : "Loading…"}</Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr><Th>Run</Th><Th>Status</Th><Th>Branch</Th><Th>Started</Th><Th>By</Th><Th></Th></Tr>
              </Thead>
              <Tbody>
                {history.map((r) => (
                  <Tr
                    key={r.id ?? r.run_id}
                    bg={sel && ((r.id && r.id === sel.id) || (r.run_id && r.run_id === sel.runId)) ? "cyan.50" : undefined}
                    _hover={{ bg: "gray.50", cursor: "pointer" }}
                    onClick={() => openRun(r)}
                  >
                    <Td fontSize="sm">{r.title}</Td>
                    <Td><RunBadge run={r} /></Td>
                    <Td fontSize="xs">{r.head_branch || ""}</Td>
                    <Td fontSize="xs">{fmt(r.run_started_at || r.created_at)}</Td>
                    <Td fontSize="xs">{r.actor || ""}</Td>
                    <Td>
                      <HStack spacing={2}>
                        {r.pending && <Text fontSize="xs" color="gray.500">waiting for GitHub…</Text>}
                        <Button size="xs" variant="outline" onClick={(e) => { e.stopPropagation(); openRun(r); }}>
                          {ACTIVE.has(r.status) ? "Follow" : "Output"}
                        </Button>
                        {r.html_url && (
                          <Link href={r.html_url} isExternal fontSize="xs" color="blue.500" onClick={(e) => e.stopPropagation()}>
                            GitHub <ExternalLinkIcon mx="2px" />
                          </Link>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </>
      )}
    </VStack>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>E2E Tests{view === "run" && sel ? ` — ${sel.run?.title || `e2e ${sel.runId}`}` : ""}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{view === "run" && sel ? runView : listView}</ModalBody>
        <TestSettingsModal
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          value={effSettings}
          defaults={settingsDefaults}
          onSave={(next) => { setSettings(next); storeSettings(next); }}
        />
        <TestPatientModal
          isOpen={patientOpen}
          onClose={() => setPatientOpen(false)}
          value={patient}
          defaults={patientDefaults}
          onSave={(next) => { setPatient(next); storePatient(next); }}
        />
        <ModalFooter>
          {view === "run" && (
            <Button variant="ghost" mr={3} onClick={() => setView("list")}>Back to all runs</Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
